import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Divider
} from '@mui/material';
import { format } from 'date-fns';

const TransferDetailsModal = ({ open, transfer, onClose }) => {
  if (!transfer) return null;

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      completed: 'success',
      rejected: 'error',
      cancelled: 'default'
    };
    return colors[status] || 'default';
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return format(new Date(date), 'MMM dd, yyyy HH:mm');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Transfer Details
        <Chip
          label={transfer.status.toUpperCase()}
          color={getStatusColor(transfer.status)}
          size="small"
          sx={{ ml: 2 }}
        />
      </DialogTitle>
      <DialogContent>
        {/* Basic Info */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>Transfer Information</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Transfer Number</Typography>
              <Typography variant="body1">{transfer.transferNumber}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Status</Typography>
              <Typography variant="body1">{transfer.status}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Material</Typography>
              <Typography variant="body1">{transfer.materialName}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Quantity</Typography>
              <Typography variant="body1">{transfer.quantity} {transfer.unit}</Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Sites */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>Sites</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">From Site</Typography>
              <Typography variant="body1">{transfer.fromSite?.siteName}</Typography>
              <Typography variant="caption">{transfer.fromSite?.location}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">To Site</Typography>
              <Typography variant="body1">{transfer.toSite?.siteName}</Typography>
              <Typography variant="caption">{transfer.toSite?.location}</Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Request Details */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>Request Details</Typography>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">Requested By</Typography>
            <Typography variant="body1">{transfer.requester?.name}</Typography>
            <Typography variant="caption">{formatDate(transfer.requestedAt)}</Typography>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">Reason</Typography>
            <Typography variant="body1">{transfer.reason}</Typography>
          </Box>
          {transfer.notes && (
            <Box>
              <Typography variant="caption" color="text.secondary">Notes</Typography>
              <Typography variant="body1">{transfer.notes}</Typography>
            </Box>
          )}
        </Box>

        {/* Review Details (if reviewed) */}
        {transfer.reviewedBy && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>Review Details</Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">Reviewed By</Typography>
                <Typography variant="body1">{transfer.reviewer?.name}</Typography>
                <Typography variant="caption">{formatDate(transfer.reviewedAt)}</Typography>
              </Box>
              {transfer.reviewComments && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Comments</Typography>
                  <Typography variant="body1">{transfer.reviewComments}</Typography>
                </Box>
              )}
            </Box>
          </>
        )}

        {/* Status History */}
        {/* Status History */}
        {transfer.statusHistory && transfer.statusHistory.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box>
              <Typography variant="h6" gutterBottom>Status History</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {transfer.statusHistory.map((history) => (
                  <Box 
                    key={history.id}
                    sx={{ 
                      p: 2, 
                      bgcolor: 'grey.50', 
                      borderRadius: 1,
                      borderLeft: `4px solid`,
                      borderLeftColor: `${getStatusColor(history.toStatus)}.main`
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Chip 
                        label={history.toStatus.toUpperCase()} 
                        size="small" 
                        color={getStatusColor(history.toStatus)}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(history.changedAt)}
                      </Typography>
                    </Box>
                    <Typography variant="body2">
                      Changed by: <strong>{history.user?.name}</strong>
                    </Typography>
                    {history.comments && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {history.comments}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransferDetailsModal;